import React, { useEffect, useState, JSX } from 'react';
import { ApiBaseData } from '@ibe/api';

import { faParking, faStar, faTicketAlt, faUtensils } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sanitize } from '@ibe/services';
import CmsOffer from '../../../Models/CMS/CmsOffer';

export interface Props {
  items: ApiBaseData[];
  textMode?: boolean;
  displayOption?: DisplayOption;
  offer?: CmsOffer;
}

export enum DisplayOption {
  PACKAGE = 'PACKAGE',
  HOTEL = 'HOTEL',
  ALL = 'ALL'
}

interface Variant {
  type: string;
  icon: JSX.Element;
}

export enum CharacteristicGroup {
  PACKTICKET = 'PACK_TICKET',
  PACKFAB = 'PACK_FAB',
  PACKOTHER = 'PACK_OTHER',
  HOTFAB = 'HOT_FAB',
  HOTPARKING = 'HOT_PARKING',
  HOTOTHER = 'HOT_OTHER',
  REGULARRELATEDSERVICE = 'REGULAR_RELATED_SERVICE'
}

export interface SegregatedCharacteristics {
  standardCharacteristics: ApiBaseData[];
  additionalCharacteristics: ApiBaseData[];
}

const OfferPillElement = ({
  offerPillText,
  textOnly
}: {
  offerPillText: string;
  textOnly: boolean;
}) => {
  return (
    <div
      className={
        textOnly
          ? `offer-service-pill servicesPills__pill`
          : `offer-service-pill servicesPills__pill-offer'}`
      }
      dangerouslySetInnerHTML={{
        __html: sanitize(offerPillText, true)
      }}
    />
  );
};

const OfferPill = ({ offerItem, textOnly }: { offerItem: CmsOffer; textOnly: boolean }) => {
  const offerPillText = textOnly
    ? offerItem?.offerPillAndCartBanner?.cartText
    : offerItem?.offerPillAndCartBanner?.pillDefinition;
  if (offerItem?.offerPillAndCartBanner?.characteristicGroupCode === null && offerPillText) {
    return <OfferPillElement offerPillText={offerPillText} textOnly={textOnly} />;
  } else {
    return <></>;
  }
};

const ServicesPills = (props: Props) => {
  const { items, textMode = false, displayOption = DisplayOption.ALL, offer } = props;
  const [standardCharacteristics, setStandardCharacteristics] = useState<ApiBaseData[]>([]);
  const [additionalCharacteristics, setAdditionalCharacteristics] = useState<ApiBaseData[]>([]);

  useEffect(() => {
    const sortedCharacteristics: SegregatedCharacteristics = sortAndFilterByGroup(items);
    setStandardCharacteristics(sortedCharacteristics.standardCharacteristics);
    setAdditionalCharacteristics(sortedCharacteristics.additionalCharacteristics);
  }, [items]);

  const sortAndFilterByGroup = (list: ApiBaseData[]): SegregatedCharacteristics => {
    const uniqueItems = [...new Map(list.map(item => [item.code, item])).values()];

    const standardCharacteristicsList: ApiBaseData[] = [];
    const additionalCharacteristicsList: ApiBaseData[] = [];

    uniqueItems.forEach(item => {
      if (
        item.group &&
        Object.values(CharacteristicGroup).includes(item.group as CharacteristicGroup)
      ) {
        standardCharacteristicsList.push(item);
      } else {
        additionalCharacteristicsList.push(item);
      }
    });

    const orderMap: { [key in CharacteristicGroup]: number } = {
      [CharacteristicGroup.PACKTICKET]: 1,
      [CharacteristicGroup.PACKFAB]: 2,
      [CharacteristicGroup.PACKOTHER]: 3,
      [CharacteristicGroup.HOTFAB]: 4,
      [CharacteristicGroup.HOTPARKING]: 5,
      [CharacteristicGroup.HOTOTHER]: 6,
      [CharacteristicGroup.REGULARRELATEDSERVICE]: 7
    };

    const sortedList = standardCharacteristicsList.toSorted((a, b) => {
      const groupA = a.group ? orderMap[a.group as CharacteristicGroup] : Number.MAX_SAFE_INTEGER;
      const groupB = b.group ? orderMap[b.group as CharacteristicGroup] : Number.MAX_SAFE_INTEGER;

      return groupA - groupB;
    });

    switch (displayOption) {
      case DisplayOption.PACKAGE:
        return {
          standardCharacteristics: sortedList.filter(
            characteristic =>
              characteristic.group === CharacteristicGroup.PACKFAB ||
              characteristic.group === CharacteristicGroup.PACKTICKET ||
              characteristic.group === CharacteristicGroup.PACKOTHER
          ),
          additionalCharacteristics: additionalCharacteristicsList
        };
      case DisplayOption.HOTEL:
        return {
          standardCharacteristics: sortedList.filter(
            characteristic =>
              characteristic.group === CharacteristicGroup.HOTFAB ||
              characteristic.group === CharacteristicGroup.HOTPARKING ||
              characteristic.group === CharacteristicGroup.HOTOTHER ||
              characteristic.group === CharacteristicGroup.REGULARRELATEDSERVICE
          ),
          additionalCharacteristics: additionalCharacteristicsList
        };
      case DisplayOption.ALL:
      default:
        return {
          standardCharacteristics: sortedList,
          additionalCharacteristics: additionalCharacteristicsList
        };
    }
  };

  const getVariant = (characteristic: ApiBaseData): Variant | null => {
    switch (characteristic.group) {
      case CharacteristicGroup.PACKTICKET:
        return { type: 'package', icon: <FontAwesomeIcon icon={faTicketAlt} /> };
      case CharacteristicGroup.PACKFAB:
        return { type: 'package', icon: <FontAwesomeIcon icon={faUtensils} /> };
      case CharacteristicGroup.PACKOTHER:
        return { type: 'package', icon: <FontAwesomeIcon icon={faStar} /> };
      case CharacteristicGroup.HOTFAB:
        return { type: 'hotel', icon: <FontAwesomeIcon icon={faUtensils} /> };
      case CharacteristicGroup.HOTPARKING:
        return { type: 'hotel', icon: <FontAwesomeIcon icon={faParking} /> };
      case CharacteristicGroup.HOTOTHER:
        return { type: 'hotel', icon: <FontAwesomeIcon icon={faStar} /> };
      case CharacteristicGroup.REGULARRELATEDSERVICE:
        return { type: 'hotel', icon: <FontAwesomeIcon icon={faStar} /> };
      default:
        return null;
    }
  };

  const Pill = ({
    characteristic,
    textOnly
  }: {
    characteristic: ApiBaseData;
    textOnly: boolean;
  }) => {
    const variant = getVariant(characteristic);
    const offerPillText = textOnly
      ? offer?.offerPillAndCartBanner?.cartText
      : offer?.offerPillAndCartBanner?.pillDefinition;
    if (
      offer?.offerPillAndCartBanner?.characteristicGroupCode === characteristic.group &&
      offerPillText
    ) {
      return <OfferPillElement offerPillText={offerPillText} textOnly={textOnly} />;
    } else {
      return variant ? (
        <div className={textOnly ? `servicesPills__pill` : `servicesPills__pill-${variant.type}`}>
          {textOnly ? null : variant.icon}
          <span> {characteristic.description}</span>
        </div>
      ) : (
        <></>
      );
    }
  };

  return items.length > 0 || offer ? (
    <div className={'servicesPills'}>
      {additionalCharacteristics?.length > 0 ? (
        additionalCharacteristics.map((characteristic, idx) => (
          <Pill
            key={`asp${characteristic.code}-${characteristic.id}_${idx}`}
            characteristic={characteristic}
            textOnly={textMode}
          />
        ))
      ) : (
        <> </>
      )}
      {standardCharacteristics?.length > 0 ? (
        standardCharacteristics.map((characteristic, idx) => (
          <Pill
            key={`sp${characteristic.code}-${characteristic.id}_${idx}`}
            characteristic={characteristic}
            textOnly={textMode}
          />
        ))
      ) : (
        <> </>
      )}
      {offer && DisplayOption.HOTEL ? (
        <OfferPill key={`op${offer.name}`} offerItem={offer} textOnly={textMode} />
      ) : (
        <> </>
      )}
    </div>
  ) : (
    <></>
  );
};

export default ServicesPills;
